import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchEmailSettings, updateEmailSettings } from './thunks';

export interface NotificationsState {
	sendDigests: boolean;
	sendEndUserAlerts: boolean;
	showSenders: boolean;
	emailDestinations: string;
	sendRemediationAlerts: boolean;
	alertsBatchingHourlyInterval: number;
	loading: boolean;
	error: string | null;
	toast: {
		type: 'success' | 'error' | 'warning' | 'info' | 'default';
		message: string;
	};
}

const initialState: NotificationsState = {
	sendDigests: false,
	sendEndUserAlerts: false,
	showSenders: false,
	emailDestinations: '',
	sendRemediationAlerts: false,
	alertsBatchingHourlyInterval: 0,
	loading: false,
	error: null,
	toast: {
		type: 'success',
		message: '',
	},
};

type NotificationsField = keyof NotificationsState;

const notificationsSlice = createSlice({
	name: 'emailSettings',
	initialState,
	reducers: {
		resetNotificationState: () => initialState,
		handleInputChange: (
			state,
			action: PayloadAction<{ name: NotificationsField; value: any }>,
		) => {
			const { name, value } = action.payload;
			(state as any)[name] = value;
		},
		pushToast: (
			state,
			action: PayloadAction<{
				type: 'success' | 'error' | 'warning' | 'info' | 'default';
				message: string;
			}>,
		) => {
			state.toast = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchEmailSettings.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchEmailSettings.fulfilled, (state, action) => {
				Object.assign(state, action.payload);
				state.loading = false;
				state.error = null;
			})
			.addCase(fetchEmailSettings.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(updateEmailSettings.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateEmailSettings.fulfilled, (state, action) => {
				Object.assign(state, action.payload);
				state.loading = false;
				state.error = null;
			})
			.addCase(updateEmailSettings.rejected, (state, action) => {
				console.error('Update email settings failed:', action.payload);
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const {
	handleInputChange,
	resetNotificationState,
	pushToast,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
